// Shared IO Mapping value evaluation — single source of truth used by the
// Live Tracking Telemetry panel and the Fleet vehicle cards.

export const interpolateCalibration = (rawValue, calibration) => {
  if (!calibration || calibration.length === 0) return rawValue;
  const val = Number(rawValue);
  if (isNaN(val)) return rawValue;
  const points = [...calibration].sort((a, b) => a.x - b.x);
  if (val <= points[0].x) return points[0].y;
  if (val >= points[points.length - 1].x) return points[points.length - 1].y;
  for (let i = 0; i < points.length - 1; i++) {
    if (val >= points[i].x && val <= points[i + 1].x) {
      const ratio = (val - points[i].x) / (points[i + 1].x - points[i].x);
      return points[i].y + ratio * (points[i + 1].y - points[i].y);
    }
  }
  return rawValue;
};

export const evaluateFormula = (rawValue, formula, decimalPlaces, dictionary, calibration) => {
  if (rawValue === undefined || rawValue === null) return '-';
  if (dictionary && Object.keys(dictionary).length > 0) {
    const key = String(rawValue);
    if (dictionary[key]) return dictionary[key];
  }
  let value = calibration && calibration.length > 0 ? interpolateCalibration(rawValue, calibration) : rawValue;
  if (formula) {
    try {
      const X = Number(value);
      if (isNaN(X)) return String(value);
      const sanitized = formula.replace(/[^0-9X+\-*/().%\s]/g, '');
      const result = new Function('X', `return ${sanitized}`)(X);
      if (decimalPlaces != null && typeof result === 'number') return result.toFixed(decimalPlaces);
      return String(result);
    } catch { return String(value); }
  }
  if (decimalPlaces != null && !isNaN(Number(value))) return Number(value).toFixed(decimalPlaces);
  return String(value);
};

export const safeIOVal = (val) => {
  if (val === null || val === undefined) return undefined;
  if (typeof val === 'object') return JSON.stringify(val);
  return val;
};

export const computeDisplayValue = (mapping, ioElements) => {
  if (!ioElements) return '-';
  const rawVal = safeIOVal(ioElements[mapping.io_id]);

  if (mapping.value_type === 'Multi-Parameter' || mapping.value_type === 'multi_parameter') {
    const val1 = safeIOVal(ioElements[mapping.io_id]);
    const val2 = safeIOVal(ioElements[mapping.secondary_io_id]);
    if (val1 != null && val2 != null) {
      if ((mapping.multi_param_mode || 'lookup') === 'lookup') {
        const sep = mapping.multi_param_separator != null ? mapping.multi_param_separator : ':';
        const key = `${val1}${sep}${val2}`;
        return (mapping.multi_param_dictionary || {})[key] || key;
      } else {
        return mapping.multi_param_template
          ? mapping.multi_param_template.replace(`[${mapping.io_id}]`, String(val1)).replace(`[${mapping.secondary_io_id}]`, String(val2))
          : `${val1}${mapping.multi_param_separator != null ? mapping.multi_param_separator : ''}${val2}`;
      }
    }
    return String(val1 ?? '-');
  }

  if (mapping.value_type === 'Logic' || mapping.value_type === 'logic') {
    return mapping.logic_mapping?.[String(rawVal)] || String(rawVal ?? '-');
  }

  const computed = evaluateFormula(rawVal, mapping.formula, mapping.decimal_places, mapping.dictionary, mapping.calibration);
  return `${computed}${mapping.units ? ` ${mapping.units}` : ''}`;
};

// Flatten nested IO elements with dot notation (e.g. Digital Matter: analog.internal_battery_mv)
export const flattenIoElements = (raw) => {
  if (!raw) return {};
  const flat = {};
  for (const [k, v] of Object.entries(raw)) {
    if (v !== null && typeof v === 'object' && !Array.isArray(v)) {
      for (const [ck, cv] of Object.entries(v)) {
        flat[`${k}.${ck}`] = cv;
      }
    } else {
      flat[k] = v;
    }
  }
  return flat;
};
